import React, { useEffect, useState, useRef } from 'react';
/**
 * ErrorSpottingRenderer — Find and correct spelling errors in text.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Search } from 'lucide-react';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
export function ErrorSpottingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const text: string = config.text || '';
  const errors: Array<{
    word: string;
    position: number;
    correction: string;
    rule?: string;
  }> = config.errors || [];
  const [corrections, setCorrections] = useState<Record<number, string>>({});
  const [activeInput, setActiveInput] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const words = text.split(/\s+/);
  useEffect(() => {
    setCorrections({});
    setActiveInput(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  // R4-BUG4: Validate that corrections are at actual error positions, not just count
  useEffect(() => {
    const errorPositions = errors.map(e => e.position);
    const correctionKeys = Object.keys(corrections).map(Number);
    const allAtValidPositions = correctionKeys.length === errors.length && correctionKeys.every(pos => errorPositions.includes(pos)) && correctionKeys.every(pos => corrections[pos]?.trim() !== '');
    onReadyChange?.(allAtValidPositions);
  }, [corrections, errors, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(corrections));
      setActiveInput(null);
    }
  }, [shouldValidate, corrections, onAnswer]);
  useEffect(() => {
    if (activeInput !== null && inputRef.current) inputRef.current.focus();
  }, [activeInput]);
  const handleWordClick = (index: number) => {
    if (shouldValidate) return;
    if (activeInput === index) {
      setActiveInput(null);
      return;
    }
    setActiveInput(index);
    if (corrections[index] === undefined) {
      setCorrections(prev => ({
        ...prev,
        [index]: words[index].replace(/[.,!?]$/, '')
      }));
    }
  };
  const handleRemoveCorrection = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (shouldValidate) return;
    const next = {
      ...corrections
    };
    delete next[index];
    setCorrections(next);
    if (activeInput === index) setActiveInput(null);
  };
  if (!text || errors.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-8532">
        <p className="text-red-700 font-bold" data-id="element-8533">
          ⚠️ Configuratie ontbreekt (text/errors)
        </p>
      </div>;
  }
  const foundCount = Object.keys(corrections).length;
  return <div className="w-full max-w-3xl mx-auto flex flex-col gap-6" data-id="element-8534">
      {/* Status Header */}
      <div className="flex items-center justify-between rounded-2xl p-4 border-2" style={{
      backgroundColor: hexToRgba(color, 0.05),
      borderColor: hexToRgba(color, 0.15)
    }} data-id="element-8535">
        <div className="flex items-center gap-3 font-bold" style={{
        color: color
      }} data-id="element-8536">
          <Search className="w-5 h-5" data-id="element-8537" />
          <span data-id="element-8538">Zoek de fouten</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-8539">
          <span className="text-sm font-medium" style={{
          color: hexToRgba(color, 0.8)
        }} data-id="element-8540">
            Gevonden:
          </span>
          <div className="bg-white px-3 py-1 rounded-lg font-bold shadow-sm border" style={{
          color: color,
          borderColor: hexToRgba(color, 0.15)
        }} data-id="element-8541">
            {foundCount} / {errors.length}
          </div>
        </div>
      </div>

      {/* Interactive Text Area */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-10 border-2 border-slate-200 shadow-sm leading-[3.5] text-lg sm:text-xl" data-id="element-8542">
        {words.map((word, index) => {
        const hasCorrection = corrections[index] !== undefined;
        const isActive = activeInput === index;
        let isCorrect = false;
        let showValidation = false;
        if (shouldValidate && hasCorrection) {
          showValidation = true;
          const errorDef = errors.find(e => e.position === index);
          if (errorDef) {
            isCorrect = corrections[index].toLowerCase().trim() === errorDef.correction.toLowerCase().trim();
          }
        }
        const match = word.match(/^([^.,!?]*)([.,!?]*)$/);
        const cleanWord = match ? match[1] : word;
        const punctuation = match ? match[2] : '';
        return <span key={index} className="relative inline-block mx-1" data-id="element-8543">
              <button onClick={() => handleWordClick(index)} disabled={shouldValidate} className={`relative px-2 py-1 rounded-lg transition-all font-medium ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:bg-slate-100'} ${hasCorrection ? 'opacity-40 line-through text-slate-500' : 'text-slate-800'}`} style={isActive ? {
            backgroundColor: hexToRgba(color, 0.05),
            '--tw-ring-color': hexToRgba(color, 0.4),
            '--tw-ring-offset-width': '0px',
            '--tw-ring-offset-color': '#fff',
            '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
            '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
            boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000'
          } as TailwindCSSProperties : undefined} data-id="element-8544">
                {cleanWord}
              </button>
              {punctuation && <span className="text-slate-800" data-id="element-8545">{punctuation}</span>}

              <AnimatePresence data-id="element-8546">
                {hasCorrection && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9,
              y: -10
            }} className="absolute left-0 -top-10 z-10 flex items-center gap-1" data-id="element-8547">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border shadow-md ${showValidation ? isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`} data-id="element-8548">
                      <input ref={isActive ? inputRef : null} type="text" value={corrections[index]} onChange={e => {
                  setCorrections(prev => ({
                    ...prev,
                    [index]: e.target.value
                  }));
                }} onFocus={() => setActiveInput(index)} disabled={shouldValidate} className={`w-24 sm:w-32 bg-transparent outline-none font-bold text-sm sm:text-base ${showValidation ? isCorrect ? 'text-emerald-700' : 'text-red-700' : 'text-slate-800'}`} placeholder="Verbetering..." data-id="element-8549" />
                      {!shouldValidate && <button onClick={e => handleRemoveCorrection(index, e)} className="p-1 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors" data-id="element-8550">
                          <XCircle className="w-4 h-4" data-id="element-8551" />
                        </button>}
                      {showValidation && (isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-8552" /> : <XCircle className="w-5 h-5 text-red-500" data-id="element-8553" />)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </span>;
      })}
      </div>
    </div>;
}